import { useEffect, useState } from "react";
import { CreditCard, Truck, Loader2 } from "lucide-react";
import { api, formatBRL, formatApiError } from "../../lib/api";
import { WhatsAppIcon, buildWhatsAppLink } from "./Header";

export function InstallmentSimulator({ initialAmount = "" }) {
  const [amount, setAmount] = useState(initialAmount);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const simulate = async (e, override) => {
    e?.preventDefault();
    setError("");
    setResult(null);
    const value = parseFloat(String(override ?? amount).replace(/\./g, "").replace(",", "."));
    if (!value || value <= 0) {
      setError("Informe um valor válido");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/payments/simulate-fees?amount=${value}`);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Quando aberto com um valor inicial (ex: total da sacola), simula na hora
  useEffect(() => {
    if (initialAmount) simulate(null, initialAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-8 rounded-3xl bg-[#F2EDE4] border border-[#E8E2D9]" data-testid="installment-simulator">
      <CreditCard className="w-7 h-7 text-[var(--brand)] mb-4" />
      <h3 className="font-display font-bold text-xl mb-2">Simule as Parcelas no Cartão</h3>
      <p className="text-sm text-[#5A534C] mb-5">Valores com as taxas da maquininha já repassadas — até 12x no crédito.</p>
      <form onSubmit={simulate} className="space-y-3">
        <input
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
          placeholder="Valor da compra (ex: 299,90)"
          data-testid="sim-amount-input"
          className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="sim-installments-button"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1816] text-white py-3 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand)] transition-colors disabled:opacity-60"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? "Calculando..." : "Simular"}
        </button>
      </form>
      {error && <p className="text-sm text-red-600 mt-3" data-testid="sim-error">{error}</p>}
      {result && (
        <ul className="mt-5 space-y-2 max-h-72 overflow-y-auto" data-testid="sim-installments-result">
          <li className="flex justify-between items-baseline text-sm bg-green-50 border border-green-200 rounded-xl px-3 py-2">
            <span className="text-green-700 font-medium">Pix ({result.pix.fee_pct}%)</span>
            <span className="font-mono text-xs font-semibold text-green-700">{formatBRL(result.pix.total)}</span>
          </li>
          <li className="flex justify-between items-baseline text-sm border-b border-[#E8E2D9] pb-2">
            <span className="text-[#5A534C]">Débito ({result.debito.fee_pct}%)</span>
            <span className="font-mono text-xs text-[#1A1816]">{formatBRL(result.debito.total)}</span>
          </li>
          {result.installments.map((p) => (
            <li key={p.installments} className="flex justify-between items-baseline text-sm border-b border-[#E8E2D9] pb-2">
              <span className="text-[#5A534C]">
                {p.installments === 1 ? "À vista" : `${p.installments}x de ${formatBRL(p.installment_amount)}`}
                <span className="text-[11px] text-[#6B645C] ml-1">({p.fee_pct}%)</span>
              </span>
              <span className="font-mono text-xs text-[#1A1816]">total {formatBRL(p.total)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FreightSimulator({ whatsappNumber }) {
  const [cep, setCep] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const calculate = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    const clean = cep.replace(/\D/g, "");
    if (clean.length !== 8) {
      setError("Informe o CEP com 8 dígitos");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/shipping/quote", { destination_cep: clean });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 rounded-3xl bg-[#F2EDE4] border border-[#E8E2D9]" data-testid="freight-simulator">
      <Truck className="w-7 h-7 text-[var(--brand)] mb-4" />
      <h3 className="font-display font-bold text-xl mb-2">Calcule seu Frete</h3>
      <p className="text-sm text-[#5A534C] mb-5">Digite seu CEP e veja as opções de entrega para a sua região.</p>
      <form onSubmit={calculate} className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          value={cep}
          onChange={(e) => setCep(e.target.value.replace(/\D/g, "").slice(0, 8))}
          placeholder="Seu CEP"
          data-testid="freight-cep-input"
          className="flex-1 bg-white border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="freight-calculate-button"
          className="inline-flex items-center gap-2 bg-[#1A1816] text-white px-6 py-3 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand)] transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Calcular"}
        </button>
      </form>
      {error && <p className="text-sm text-red-600 mt-3" data-testid="freight-error">{error}</p>}

      {result?.mode === "live" && (
        <ul className="mt-5 space-y-2" data-testid="freight-result">
          {result.options.length === 0 && (
            <li className="text-sm text-[#6B645C]">Nenhuma opção disponível para este CEP. Fale com a loja.</li>
          )}
          {result.options.map((o) => (
            <li key={o.id} className="flex justify-between items-baseline text-sm border-b border-[#E8E2D9] pb-2">
              <span className="text-[#5A534C]">
                {o.name}{o.company ? ` · ${o.company}` : ""}
                {o.delivery_days && <span className="block text-xs text-[#6B645C]">até {o.delivery_days} dias úteis</span>}
              </span>
              <span className="font-mono text-sm font-semibold text-[#1A1816]">{formatBRL(o.price)}</span>
            </li>
          ))}
        </ul>
      )}

      {result?.mode === "estimate" && (
        <div className="mt-5 text-sm" data-testid="freight-estimate">
          {result.is_local ? (
            <p className="text-[#1A1816] bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              Você é de <strong>{result.city}/{result.uf}</strong> — nossa cidade! Entrega rápida com valor a combinar pelo WhatsApp.
            </p>
          ) : (
            <p className="text-[#1A1816] bg-white border border-[#E8E2D9] rounded-xl px-4 py-3">
              Frete para <strong>{result.city}/{result.uf}</strong>: calculamos na hora pelo WhatsApp com o melhor valor dos Correios para o seu CEP.
            </p>
          )}
          <a
            href={buildWhatsAppLink(whatsappNumber, `Olá! Quero calcular o frete para o CEP ${cep.replace(/(\d{5})(\d{3})/, "$1-$2")} (${result.city}/${result.uf}).`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="freight-whatsapp-button"
            className="mt-3 inline-flex items-center gap-2 bg-[var(--brand)] text-white px-5 py-2.5 rounded-full font-display text-xs uppercase tracking-wider font-semibold hover:bg-[var(--brand-dark)] transition-colors"
          >
            <WhatsAppIcon /> Combinar Frete no WhatsApp
          </a>
        </div>
      )}
    </div>
  );
}

export default function Simulators({ whatsappNumber }) {
  return (
    <section id="simuladores" className="py-4 px-6 max-w-6xl mx-auto">
      <div className="mb-10">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--brand)] block mb-2">Sem Compromisso</span>
        <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight">Simule antes de comprar</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <InstallmentSimulator />
        <FreightSimulator whatsappNumber={whatsappNumber} />
      </div>
    </section>
  );
}
